import { useEffect, useRef, useState, useMemo } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Site } from '@/types/site';
import { supabase } from '@/integrations/supabase/client';
import { useZoneStore } from '@/stores/zoneStore';
import { useSystemStore } from '@/stores/systemStore';
import { useAssetsStore } from '@/stores/assetsStore';
import { useLinkStore } from '@/stores/linkStore';
import { useRisks, useIncidents, useControls, useTasks } from '@/hooks/queries';
import { useAuth } from '@/hooks/useAuth';
import { calculateSiteMetrics, getRiskLevelColor } from '@/utils/siteMetrics';

interface SiteMapProps {
  sites: Site[];
  onSiteClick?: (site: Site) => void;
  onMapClick?: (coordinates: { lat: number; lon: number }) => void;
  className?: string;
}

export function SiteMap({ sites, onSiteClick, onMapClick, className = '' }: SiteMapProps) {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const markersRef = useRef<mapboxgl.Marker[]>([]);
  const pickerMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const boundsSetRef = useRef<boolean>(false);
  const markersCreatedForSitesRef = useRef<Set<string>>(new Set());
  const [mapboxToken, setMapboxToken] = useState<string | null>(null);
  const [tokenError, setTokenError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [mapReady, setMapReady] = useState(false);
  const [showDebug, setShowDebug] = useState(false);
  
  const { zones, loadZones } = useZoneStore();
  const { systems, loadSystems } = useSystemStore();
  const { nodes, loadAssets } = useAssetsStore();
  const { links, loadLinks } = useLinkStore();
  const { organization } = useAuth();
  
  const { risks } = useRisks(organization?.id);
  const { incidents } = useIncidents(organization?.id);
  const { controls } = useControls(organization?.id);
  const { tasks } = useTasks(organization?.id);
  
  // Load all data for metrics calculation
  useEffect(() => {
    if (!onMapClick) { // Only load for display mode, not picker mode
      setLoading(true);
      Promise.all([
        loadZones(),
        loadSystems(),
        loadAssets(),
        loadLinks()
      ]).finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [onMapClick, loadZones, loadSystems, loadAssets, loadLinks]);
  
  // Convert nodes to endpoints - memoized for stability
  const endpoints = useMemo(() => {
    return nodes.map(node => ({
      id: node.id,
      org_id: organization?.id || '',
      name: node.data.label,
      type: node.data.type as string,
      system_id: (node.data.system_id as string | undefined) || undefined,
      site_id: (node.data.site_id as string | undefined) || undefined,
      created_at: '',
      updated_at: '',
    }));
  }, [nodes, organization?.id]);
  
  // Calculate metrics - memoized to prevent unnecessary recalculations
  const siteMetrics = useMemo(() => {
    if (onMapClick || !risks || !incidents || !controls || !tasks) return [];
    
    return sites
      .filter(site => site.metadata?.coordinates?.lat && site.metadata?.coordinates?.lon)
      .map(site => 
        calculateSiteMetrics(
          site,
          zones,
          systems,
          endpoints,
          links,
          risks,
          controls,
          incidents,
          tasks
        )
      );
  }, [sites, zones, systems, endpoints, links, risks, incidents, controls, tasks, onMapClick]);

  // Fetch Mapbox token from Supabase edge function
  useEffect(() => {
    const fetchToken = async () => {
      console.log('SiteMap: Fetching Mapbox token...');
      try {
        const { data, error } = await supabase.functions.invoke('get-mapbox-token');
        
        if (error) {
          console.error('SiteMap: Error fetching Mapbox token:', error);
          setTokenError('Failed to load map token');
          return;
        }
        
        if (data?.token) {
          console.log('SiteMap: Mapbox token received');
          setMapboxToken(data.token);
        } else {
          console.error('SiteMap: No token in response');
          setTokenError('Map token not configured');
        }
      } catch (error) {
        console.error('SiteMap: Exception fetching Mapbox token:', error);
        setTokenError('Failed to load map');
      }
    };
    
    fetchToken();
  }, []);

  // Initialize map when token is available
  useEffect(() => {
    if (!mapContainer.current) {
      console.log('SiteMap: Map container not available yet');
      return;
    }
    if (!mapboxToken) {
      console.log('SiteMap: Waiting for Mapbox token...');
      return;
    }
    
    console.log('SiteMap: Initializing map with token');
    
    try {
      mapboxgl.accessToken = mapboxToken;
      
      // Use existing coordinates if available, otherwise default to Stockholm
      const defaultCenter: [number, number] = [18.0686, 59.3293];
      const initialCenter: [number, number] = 
        sites.length > 0 && sites[0].metadata?.coordinates?.lon && sites[0].metadata?.coordinates?.lat
          ? [sites[0].metadata.coordinates.lon, sites[0].metadata.coordinates.lat]
          : defaultCenter;

      console.log('SiteMap: Creating map at center:', initialCenter);

      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: initialCenter,
        zoom: onMapClick ? 12 : 4, // Zoom in more for picker mode
      });

      map.current.addControl(new mapboxgl.NavigationControl(), 'top-right');

      // Mark map as ready when it loads
      map.current.on('load', () => {
        console.log('SiteMap: Map loaded and ready');
        setMapReady(true);
      });

      map.current.on('error', (e) => {
        console.error('SiteMap: Mapbox error:', e);
      });

      // Add draggable marker for coordinate picking mode
      if (onMapClick) {
        const el = document.createElement('div');
        el.className = 'picker-marker';
        el.style.cssText = `
          width: 40px;
          height: 40px;
          background-color: hsl(var(--primary));
          border: 3px solid white;
          border-radius: 50% 50% 50% 0;
          transform: rotate(-45deg);
          cursor: move;
          box-shadow: 0 3px 10px rgba(0,0,0,0.3);
        `;
        
        const innerDot = document.createElement('div');
        innerDot.style.cssText = `
          width: 12px;
          height: 12px;
          background-color: white;
          border-radius: 50%;
          position: absolute;
          top: 50%;
          left: 50%;
          transform: translate(-50%, -50%);
        `;
        el.appendChild(innerDot);

        pickerMarkerRef.current = new mapboxgl.Marker({
          element: el,
          draggable: true,
        })
          .setLngLat(map.current.getCenter())
          .addTo(map.current);

        pickerMarkerRef.current.on('dragend', () => {
          const lngLat = pickerMarkerRef.current!.getLngLat();
          onMapClick({ lat: lngLat.lat, lon: lngLat.lng });
        });

        pickerMarkerRef.current.on('drag', () => {
          const lngLat = pickerMarkerRef.current!.getLngLat();
          onMapClick({ lat: lngLat.lat, lon: lngLat.lng });
        });
      }
    } catch (error) {
      console.error('Mapbox initialization error:', error);
    }

    return () => {
      // Clean up markers
      markersRef.current.forEach(marker => marker.remove());
      markersRef.current = [];
      markersCreatedForSitesRef.current.clear();
      
      if (pickerMarkerRef.current) {
        pickerMarkerRef.current.remove();
        pickerMarkerRef.current = null;
      }
      if (map.current && map.current.getContainer()) {
        map.current.remove();
        map.current = null;
      }
      setMapReady(false);
    };
  }, [mapboxToken, onMapClick]);

  // Create markers ONCE when map is ready and we have data
  useEffect(() => {
    if (!map.current || !mapReady || loading || siteMetrics.length === 0) return;
    
    // Update picker marker position if coordinates exist
    if (onMapClick && pickerMarkerRef.current && sites.length > 0) {
      const site = sites[0];
      if (site.metadata?.coordinates?.lat && site.metadata?.coordinates?.lon) {
        pickerMarkerRef.current.setLngLat([
          site.metadata.coordinates.lon,
          site.metadata.coordinates.lat
        ]);
      }
      return; // Don't create regular markers in picker mode
    }

    // Don't add regular markers in picker mode
    if (onMapClick) return;

    // Get sites with coordinates
    const sitesWithCoords = sites.filter(site => 
      site.metadata?.coordinates?.lat && site.metadata?.coordinates?.lon
    );
    
    // Check if we need to create any new markers
    const sitesNeedingMarkers = sitesWithCoords.filter(
      site => !markersCreatedForSitesRef.current.has(site.id)
    );
    
    // If all sites already have markers, do nothing
    if (sitesNeedingMarkers.length === 0) return;
    
    console.log(`Creating markers for ${sitesNeedingMarkers.length} new sites`);

    sitesNeedingMarkers.forEach(site => {
      const coords = site.metadata!.coordinates!;
      const metrics = siteMetrics.find(m => m.siteId === site.id);
      
      // Create marker element with metrics-based styling
      const el = document.createElement('div');
      el.className = 'site-marker';
      
      if (metrics) {
        const riskColor = getRiskLevelColor(metrics.overallRiskLevel);
        const criticalIssues = metrics.riskCount + metrics.incidentCount;
        
        el.style.cssText = `
          width: 34px;
          height: 34px;
          background-color: ${riskColor};
          border: 3px solid white;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 3px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          position: relative;
        `;
        el.innerHTML = '🏢';
        
        // Add badge for critical issues
        if (criticalIssues > 0) {
          const badge = document.createElement('div');
          badge.style.cssText = `
            position: absolute;
            top: -6px;
            right: -6px;
            background: #dc2626;
            color: white;
            border-radius: 50%;
            width: 18px;
            height: 18px;
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 9px;
            font-weight: bold;
            border: 2px solid white;
            box-shadow: 0 2px 4px rgba(0,0,0,0.3);
          `;
          badge.textContent = criticalIssues.toString();
          el.appendChild(badge);
        }
      } else {
        // Fallback for sites without metrics
        el.style.cssText = `
          width: 34px;
          height: 34px;
          background-color: hsl(var(--primary));
          border: 3px solid white;
          border-radius: 50%;
          cursor: pointer;
          box-shadow: 0 3px 6px rgba(0,0,0,0.3);
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 16px;
          position: relative;
        `;
        el.innerHTML = '🏢';
      }

      // Create enhanced popup
      const popupContent = metrics 
        ? `
          <div style="padding: 10px; font-family: system-ui, -apple-system, sans-serif;">
            <h3 style="font-weight: 700; margin-bottom: 6px; font-size: 14px;">📍 ${site.name}</h3>
            ${site.address ? `<p style="font-size: 11px; color: #666; margin-bottom: 8px;">${site.address}</p>` : ''}
            
            <div style="border-top: 1px solid #e5e7eb; padding-top: 6px; margin-top: 6px;">
              <div style="font-size: 10px; font-weight: 600; color: #374151; margin-bottom: 4px;">INFRASTRUCTURE</div>
              <div style="font-size: 11px; color: #4b5563; line-height: 1.5;">
                ${metrics.zoneCount} Zones • ${metrics.systemCount} Systems • ${metrics.endpointCount} Endpoints
              </div>
            </div>
            
            <div style="border-top: 1px solid #e5e7eb; padding-top: 6px; margin-top: 6px;">
              <div style="font-size: 10px; font-weight: 600; color: #374151; margin-bottom: 4px;">COMPLIANCE</div>
              <div style="font-size: 11px; color: #4b5563; line-height: 1.5;">
                ${metrics.riskCount > 0 ? `<div style="color: #dc2626;">⚠️ ${metrics.riskCount} Risk${metrics.riskCount !== 1 ? 's' : ''}</div>` : '✓ No risks'}
                ${metrics.incidentCount > 0 ? `<div style="color: #dc2626;">⚠️ ${metrics.incidentCount} Incident${metrics.incidentCount !== 1 ? 's' : ''}</div>` : ''}
                ${metrics.controlCount > 0 ? `<div>${metrics.controlCount} Control Gap${metrics.controlCount !== 1 ? 's' : ''}</div>` : ''}
                ${metrics.taskCount > 0 ? `<div>${metrics.taskCount} Pending Task${metrics.taskCount !== 1 ? 's' : ''}</div>` : ''}
              </div>
            </div>
          </div>
        `
        : `
          <div style="padding: 8px;">
            <strong>${site.name}</strong><br/>
            ${site.address || 'No address'}
          </div>
        `;

      const marker = new mapboxgl.Marker({
        element: el,
        anchor: 'center'
      })
        .setLngLat([coords.lon, coords.lat])
        .setPopup(new mapboxgl.Popup({ offset: 25, maxWidth: '280px' }).setHTML(popupContent))
        .addTo(map.current!);

      if (onSiteClick) {
        el.addEventListener('click', () => onSiteClick(site));
      }

      markersRef.current.push(marker);
      markersCreatedForSitesRef.current.add(site.id);
    });

    // Fit map to markers ONLY ONCE on initial load
    if (sitesNeedingMarkers.length > 0 && !boundsSetRef.current) {
      const bounds = new mapboxgl.LngLatBounds();
      sitesWithCoords.forEach(site => {
        const coords = site.metadata!.coordinates!;
        bounds.extend([coords.lon, coords.lat]);
      });
      map.current?.fitBounds(bounds, { padding: 50, maxZoom: 12 });
      boundsSetRef.current = true;
    }
  }, [mapReady, loading, sites, siteMetrics, onMapClick, onSiteClick]);

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Debug Panel */}
      {showDebug && (
        <div className="absolute top-2 right-2 z-20 bg-black/80 text-white text-xs p-3 rounded-lg max-w-xs">
          <div className="flex justify-between items-start mb-2">
            <div className="font-bold">Map Debug Info</div>
            <button 
              onClick={() => setShowDebug(false)}
              className="text-white/60 hover:text-white ml-2"
            >
              ✕
            </button>
          </div>
          <div className="space-y-1">
            <div>Token: {mapboxToken ? '✓ Loaded' : tokenError ? '✗ Error' : '⏳ Loading...'}</div>
            <div>Container: {mapContainer.current ? '✓ Ready' : '✗ Not ready'}</div>
            <div>Map Instance: {map.current ? '✓ Created' : '✗ Not created'}</div>
            <div>Map Ready: {mapReady ? '✓ Yes' : '✗ No'}</div>
            <div>Loading: {loading ? '⏳ Yes' : '✓ No'}</div>
            <div>Sites: {sites.length} total</div>
            <div>Sites w/ coords: {sites.filter(s => s.metadata?.coordinates).length}</div>
            <div>Metrics: {siteMetrics.length}</div>
            <div>Markers: {markersRef.current.length}</div>
            {tokenError && <div className="text-red-400 mt-2">Error: {tokenError}</div>}
          </div>
        </div>
      )}
      
      {tokenError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10 rounded-lg">
          <div className="text-center p-4">
            <p className="text-destructive font-medium">{tokenError}</p>
            <p className="text-sm text-muted-foreground mt-2">
              Please configure MAPBOX_PUBLIC_TOKEN in Supabase Edge Function Secrets
            </p>
          </div>
        </div>
      )}
      {!mapboxToken && !tokenError && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10 rounded-lg">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading map...</p>
          </div>
        </div>
      )}
      {loading && mapboxToken && (
        <div className="absolute inset-0 flex items-center justify-center bg-muted/50 z-10 rounded-lg">
          <div className="text-center">
            <div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full mx-auto mb-2" />
            <p className="text-sm text-muted-foreground">Loading data...</p>
          </div>
        </div>
      )}
      <div ref={mapContainer} className="absolute inset-0 rounded-lg" />
    </div>
  );
}
